/**
 * Libro NUEVO desde la app (para quien empieza): el mismo formato V4 que construye apps/shared/WorkbookV4.js, pero
 * como un plan de llamadas a la API de Google Sheets (puro: se prueba sin red).
 *  1) crear la hoja (pestañas, idioma es_ES y zona horaria; las internas ocultas),
 *  2) escribir cabeceras, fórmulas (las de FORMULAS, idénticas) y los datos iniciales (cuentas y categorías),
 *  3) dar formato: casillas, desplegables, formatos de fecha e importe, columnas calculadas protegidas (solo aviso).
 * Las fórmulas van con ';' porque el libro es es_ES (igual que «rewrite-formulas»).
 */
import { FORMULAS, HEADERS, WORKBOOK_VERSION } from './schema.js';
import { isoToSerial } from './cells.js';

export const NEW_WORKBOOK_TABS = ['Resumen', 'Presupuesto', 'Notas', 'Movimientos', 'Divisiones', 'Cuentas', 'Categorías', 'Patrimonio',
  'Inversiones', 'Buscar', 'Histórico', 'Archivo', 'Léeme', '_Meta', '_Reglas', '_Alias', '_Control'] as const;
const MOVEMENT_ROWS = 3000;
const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const MONEY = '#,##0.00 €', DATE = 'dd/mm/yyyy';

/** Categorías de partida: genéricas (nada de nadie). Cada uno las cambia a su gusto. */
export const STARTER_CATEGORIES: Array<[string, 'Gasto' | 'Ingreso' | 'No cuenta', 'Fijo' | 'Variable' | '', string]> = [
  ['Nómina', 'Ingreso', 'Fijo', 'nomina'],
  ['Intereses', 'Ingreso', 'Variable', 'intereses'],
  ['Otros ingresos', 'Ingreso', 'Variable', ''],
  ['Vivienda', 'Gasto', 'Fijo', 'alquiler,hipoteca,prestamo hipotecario,comunidad'],
  ['Suministros', 'Gasto', 'Fijo', 'luz,agua,gas,iberdrola,endesa,naturgy'],
  ['Teléfono e internet', 'Gasto', 'Fijo', 'fibra,movistar,vodafone,orange,digi'],
  ['Seguros', 'Gasto', 'Fijo', 'seguro,mapfre,mutua'],
  ['Suscripciones', 'Gasto', 'Fijo', 'netflix,spotify,hbo,disney'],
  ['Supermercado', 'Gasto', 'Variable', 'mercadona,lidl,carrefour,alcampo,aldi,eroski,ahorramas'],
  ['Restaurante', 'Gasto', 'Variable', 'restaurante,bar ,cafe,cafeteria,burger,pizzeria'],
  ['Transporte', 'Gasto', 'Variable', 'renfe,metro,emt,cabify,uber,bolt,taxi'],
  ['Gasolina', 'Gasto', 'Variable', 'gasolinera,repsol,cepsa,galp,shell'],
  ['Coche', 'Gasto', 'Variable', 'taller,itv,parking,neumaticos'],
  ['Ocio', 'Gasto', 'Variable', 'cine,concierto,teatro,entradas'],
  ['Viajes', 'Gasto', 'Variable', 'hotel,vuelo,booking,airbnb,ryanair,iberia,vueling'],
  ['Ropa', 'Gasto', 'Variable', 'zara,primark,ropa,zapatos'],
  ['Salud', 'Gasto', 'Variable', 'farmacia,dentista,optica,clinica'],
  ['Deporte', 'Gasto', 'Variable', 'gimnasio,decathlon'],
  ['Hogar', 'Gasto', 'Variable', 'ikea,leroy merlin,ferreteria,muebles'],
  ['Compras online', 'Gasto', 'Variable', 'amazon,aliexpress'],
  ['Regalos', 'Gasto', 'Variable', 'regalo,jugueteria'],
  ['Educación', 'Gasto', 'Variable', 'colegio,academia,libreria,curso'],
  ['Otros gastos', 'Gasto', 'Variable', ''],
  ['Transferencia interna', 'No cuenta', '', 'traspaso,transferencia interna'],
  ['Inversión', 'No cuenta', '', 'aportacion'],
  ['Dividido', 'No cuenta', '', ''],
];

/** Tipos de cuenta que ofrece el asistente (texto libre en la hoja). */
export const ACCOUNT_TYPES = ['Corriente', 'Ahorro', 'Tarjeta comida', 'Efectivo', 'Inversión'] as const;

export interface NewAccount {
  name: string; bank: string; type: string;
  /** Parte que es tuya (0–1): 0,5 en una cuenta conjunta. */
  share: number;
  /** Cuenta en tu liquidez y patrimonio (el efectivo suele ir fuera). */
  inNetWorth: boolean;
  /** Saldo en la fecha de inicio (lo que había antes del primer movimiento que vayas a apuntar). */
  opening: number;
  openingDate: string;
}

/** Problema en los datos del asistente, o '' si se puede crear. */
export function checkNewAccounts(accounts: NewAccount[]): string {
  if (!accounts.length) return 'Añade al menos una cuenta';
  const names = accounts.map(a => a.name.trim().toLowerCase());
  if (names.some(n => !n)) return 'Pon nombre a cada cuenta';
  if (names.some(n => n.length > 40)) return 'El nombre de una cuenta es demasiado largo (máximo 40 letras)';
  if (new Set(names).size !== names.length) return 'Hay dos cuentas con el mismo nombre';
  if (accounts.some(a => !(a.share > 0 && a.share <= 1))) return 'La parte tuya de una cuenta debe estar entre 1 % y 100 %';
  if (accounts.some(a => !Number.isFinite(a.opening))) return 'Revisa los saldos iniciales';
  if (accounts.some(a => !/^\d{4}-\d{2}-\d{2}$/.test(a.openingDate))) return 'Falta la fecha del saldo inicial';
  return '';
}

type Cell = string | number | boolean;
export interface SheetsCreateBody {
  properties: { title: string; locale: string; timeZone: string };
  sheets: Array<{ properties: { sheetId: number; title: string; index: number; hidden?: boolean; gridProperties: { rowCount: number; columnCount: number; frozenRowCount?: number } } }>;
}
export interface NewWorkbookPlan {
  create: SheetsCreateBody;
  /** values:batchUpdate con USER_ENTERED (fórmulas y datos). */
  values: Array<{ range: string; values: Cell[][] }>;
  /** spreadsheets:batchUpdate (formato). */
  requests: object[];
}

const sheetId = (tab: string) => NEW_WORKBOOK_TABS.indexOf(tab as (typeof NEW_WORKBOOK_TABS)[number]) + 1;
const colIndex = (letters: string) => [...letters].reduce((n, c) => n * 26 + c.charCodeAt(0) - 64, 0) - 1;
/** 'A2:B10', 'H1:I' (hasta el final) o 'B6' → GridRange de la API. */
export function gridRange(tab: string, a1: string) {
  const [from, to = from] = a1.split(':') as [string, string?];
  const p = (s: string) => { const m = /^([A-Z]+)(\d*)$/.exec(s)!; return { col: colIndex(m[1]!), row: m[2] ? Number(m[2]) : null }; };
  const a = p(from), b = p(to!);
  return { sheetId: sheetId(tab), startColumnIndex: a.col, endColumnIndex: b.col + 1, startRowIndex: (a.row ?? 1) - 1,
    ...(b.row !== null ? { endRowIndex: b.row } : {}) };
}

/** Fila 1 de una pestaña: la cabecera, salvo donde hay fórmula (que ya lleva su título). */
const headerRow = (tab: keyof typeof HEADERS, extra: Record<string, Cell> = {}): Cell[] => {
  const labels: Cell[] = [...HEADERS[tab]];
  const width = Math.max(labels.length, ...Object.keys(extra).map(c => colIndex(c) + 1),
    ...Object.keys(FORMULAS).filter(k => k.startsWith(`${tab}!`) && /\D1$/.test(k)).map(k => colIndex(/!([A-Z]+)1$/.exec(k)![1]!) + 1));
  return Array.from({ length: width }, (_, i) => {
    const col = String.fromCharCode(65 + i);
    return FORMULAS[`${tab}!${col}1`] ?? extra[col] ?? labels[i] ?? '';
  });
};

export function newWorkbookPlan(o: { title: string; today: string; accounts: NewAccount[]; categories?: Array<[string, string, string, string]> }): NewWorkbookPlan {
  const categories = o.categories ?? STARTER_CATEGORIES;
  const year = Number(o.today.slice(0, 4)), month = Number(o.today.slice(5, 7));
  const internal = (t: string) => t.startsWith('_');
  const create: SheetsCreateBody = {
    properties: { title: o.title, locale: 'es_ES', timeZone: 'Europe/Madrid' },
    sheets: NEW_WORKBOOK_TABS.map((title, index) => ({ properties: {
      sheetId: sheetId(title), title, index, ...(internal(title) ? { hidden: true } : {}),
      gridProperties: { rowCount: title === 'Movimientos' ? MOVEMENT_ROWS : 1000, columnCount: 26,
        ...(title in HEADERS || title === 'Inversiones' ? { frozenRowCount: 1 } : {}) } } })),
  };
  const f = (cell: string) => FORMULAS[cell]!;
  const values: NewWorkbookPlan['values'] = [
    // Tablas: cabecera (y fórmulas de la fila 1)
    { range: "'Categorías'!A1", values: [headerRow('Categorías'), ...categories.map(c => [...c])] },
    { range: 'Cuentas!A1', values: [headerRow('Cuentas'), ...o.accounts.map(a => [a.name.trim(), a.bank.trim(), a.type, a.share, a.inNetWorth,
      Math.round(a.opening * 100) / 100, isoToSerial(a.openingDate)])] },
    { range: 'Cuentas!N2', values: [[f('Cuentas!N2')]] },
    { range: 'Movimientos!A1', values: [headerRow('Movimientos', { O: 'Id' })] },
    { range: 'Divisiones!A1', values: [headerRow('Divisiones')] },
    { range: 'Presupuesto!A1', values: [headerRow('Presupuesto')] },
    { range: 'Notas!A1', values: [headerRow('Notas')] },
    { range: 'Patrimonio!A1', values: [headerRow('Patrimonio')] },
    { range: 'Archivo!A1', values: [headerRow('Archivo')] },
    { range: "'Histórico'!A1", values: [headerRow('Histórico')] },
    { range: '_Meta!A1', values: [headerRow('_Meta')] },
    { range: '_Reglas!A1', values: [headerRow('_Reglas')] },
    { range: '_Alias!A1', values: [headerRow('_Alias')] },
    // Inversiones: dos tablas y los totales
    { range: 'Inversiones!A1', values: [['Fecha', 'Fondo', 'Importe (+aporta / −reembolsa)', 'Participaciones', '', 'Fondo', f('Inversiones!G1'), 'Precio', 'Fecha precio',
      f('Inversiones!J1'), f('Inversiones!K1'), f('Inversiones!L1'), '', 'Valor total'], ['', '', '', '', '', '', '', '', '', '', '', '', '', f('Inversiones!N2')],
      ['', '', '', '', '', '', '', '', '', '', '', '', '', 'Aportado total'], ['', '', '', '', '', '', '', '', '', '', '', '', '', f('Inversiones!N4')]] },
    // Resumen
    { range: 'Resumen!A1', values: [['Resumen']] },
    { range: 'Resumen!A3', values: [['Año', year, '', 'Ingresos', 'Gastos', 'Ahorro', 'Tasa de ahorro', 'Patrimonio actual'],
      ['Mes', month, '', f('Resumen!D4'), f('Resumen!E4'), f('Resumen!F4'), f('Resumen!G4'), f('Resumen!H4')], ['Periodo', f('Resumen!B5')]] },
    { range: 'Resumen!A7', values: [['Presupuesto del mes (gastos atribuibles)', '', '', '', '', '', '', 'Año completo (atribuible)'],
      ['Categoría', 'Presupuesto', 'Real', 'Queda', '% usado', '', '', 'Categoría', ...MONTHS, 'Total'],
      [f('Resumen!A9'), f('Resumen!B9'), f('Resumen!C9'), f('Resumen!D9'), f('Resumen!E9'), f('Resumen!F9'), '', 'Ahorro', f('Resumen!I9'), ...Array(11).fill(''), f('Resumen!U9')],
      ['', '', '', '', '', '', '', f('Resumen!H10'), f('Resumen!I10'), ...Array(11).fill(''), f('Resumen!U10')]] },
    // Buscar
    { range: 'Buscar!A1', values: [['Buscar movimientos (actuales y archivados)'], [''], ['Texto'], ['Desde'], ['Hasta'], ['Categoría'], ['Cuenta'], [''],
      ['Fecha', 'Concepto', 'Importe', 'Cuenta', 'Categoría', 'Grupo', 'Nota'], [f('Buscar!A10')]] },
    { range: "'Léeme'!A1", values: LEEME.map(l => [l]) },
    // Control: versión, archivo y años con movimientos
    { range: '_Control!A1', values: [['Versión del libro', WORKBOOK_VERSION], ['Revisión del archivo', 0], ['Huella del archivo', f('_Control!B3')],
      ['Años archivados', ''], ['Diario del archivo', '']] },
    { range: '_Control!A10', values: [['Años con movimientos', f('_Control!B10')]] },
  ];

  const requests: object[] = [];
  const fmt = (tab: string, a1: string, pattern: string, type = pattern.includes('yyyy') ? 'DATE' : pattern === '@' ? 'TEXT' : pattern.includes('%') ? 'PERCENT' : 'NUMBER') =>
    requests.push({ repeatCell: { range: gridRange(tab, a1), cell: { userEnteredFormat: { numberFormat: { type, pattern } } }, fields: 'userEnteredFormat.numberFormat' } });
  const style = (tab: string, a1: string, bg: { red: number; green: number; blue: number }) =>
    requests.push({ repeatCell: { range: gridRange(tab, a1), cell: { userEnteredFormat: { textFormat: { bold: true }, backgroundColor: bg } }, fields: 'userEnteredFormat(textFormat,backgroundColor)' } });
  const checkbox = (tab: string, a1: string) => requests.push({ setDataValidation: { range: gridRange(tab, a1), rule: { condition: { type: 'BOOLEAN' } } } });
  const listFrom = (tab: string, a1: string, source: string) =>
    requests.push({ setDataValidation: { range: gridRange(tab, a1), rule: { condition: { type: 'ONE_OF_RANGE', values: [{ userEnteredValue: `=${source}` }] }, strict: false, showCustomUi: true } } });
  const listOf = (tab: string, a1: string, items: string[], strict: boolean) =>
    requests.push({ setDataValidation: { range: gridRange(tab, a1), rule: { condition: { type: 'ONE_OF_LIST', values: items.map(v => ({ userEnteredValue: v })) }, strict, showCustomUi: true } } });
  const calculated = (tab: string, a1: string) => requests.push({ addProtectedRange: { protectedRange: { range: gridRange(tab, a1), description: 'Calculado: no escribir', warningOnly: true } } });
  const widths = (tab: string, px: number[]) => px.forEach((pixelSize, i) => pixelSize && requests.push({ updateDimensionProperties: {
    range: { sheetId: sheetId(tab), dimension: 'COLUMNS', startIndex: i, endIndex: i + 1 }, properties: { pixelSize }, fields: 'pixelSize' } }));
  const blue = { red: 0.91, green: 0.933, blue: 0.969 }, grey = { red: 0.933, green: 0.933, blue: 0.933 };

  // Cabeceras
  for (const tab of [...Object.keys(HEADERS), 'Inversiones']) style(tab, 'A1:Z1', blue);
  style('Movimientos', 'J1:P1', grey); style('Divisiones', 'E1:K1', grey); style('Cuentas', 'H1:I1', grey); style('Cuentas', 'L1:L1', grey);
  // Movimientos
  const last = MOVEMENT_ROWS;
  checkbox('Movimientos', `F2:F${last}`);
  fmt('Movimientos', `A2:A${last}`, DATE); fmt('Movimientos', `C2:C${last}`, MONEY); fmt('Movimientos', `N2:N${last}`, MONEY);
  fmt('Movimientos', `I2:I${last}`, '@'); fmt('Movimientos', `O2:O${last}`, '@');
  listFrom('Movimientos', `D2:D${last}`, 'Cuentas!A2:A50');
  listFrom('Movimientos', `E2:E${last}`, "'Categorías'!A2:A300");
  calculated('Movimientos', 'J1:N'); calculated('Movimientos', 'P1:P');
  widths('Movimientos', [90, 300, 90, 130, 170, 85, 140, 220, 95, 170, 90, 85, 85, 90, 130, 150]);
  // Cuentas
  checkbox('Cuentas', 'E2:E50');
  fmt('Cuentas', 'D2:D50', '0%'); fmt('Cuentas', 'F2:F50', MONEY); fmt('Cuentas', 'H2:L50', MONEY); fmt('Cuentas', 'N2:N2', MONEY);
  fmt('Cuentas', 'G2:G50', DATE); fmt('Cuentas', 'K2:K50', DATE);
  calculated('Cuentas', 'H1:I'); calculated('Cuentas', 'L1:L');
  widths('Cuentas', [150, 120, 110, 80, 100, 110, 120, 100, 120, 120, 120, 100, 20, 140]);
  // Categorías
  listOf('Categorías', 'B2:B300', ['Gasto', 'Ingreso', 'No cuenta'], true);
  listOf('Categorías', 'C2:C300', ['Fijo', 'Variable'], false);
  widths('Categorías', [200, 100, 100, 420]);
  // Divisiones, Presupuesto, Notas
  fmt('Divisiones', 'A2:A1000', '@'); fmt('Divisiones', 'C2:C1000', MONEY); fmt('Divisiones', 'E2:E1000', DATE); fmt('Divisiones', 'J2:J1000', MONEY);
  listFrom('Divisiones', 'B2:B1000', "'Categorías'!A2:A300"); calculated('Divisiones', 'E1:K');
  checkbox('Presupuesto', 'P2:P500'); fmt('Presupuesto', 'C2:O500', '#,##0.00'); listFrom('Presupuesto', 'B2:B500', "'Categorías'!A2:A300");
  fmt('Notas', 'A2:A1000', '@'); listFrom('Notas', 'B2:B1000', "'Categorías'!A2:A300");
  // Patrimonio, Inversiones, Archivo
  fmt('Patrimonio', 'A2:A600', '@'); fmt('Patrimonio', 'B2:D600', MONEY);
  fmt('Inversiones', 'A2:A1000', DATE); fmt('Inversiones', 'I2:I100', DATE); fmt('Inversiones', 'C2:C1000', MONEY);
  fmt('Inversiones', 'D2:D1000', '#,##0.0000'); fmt('Inversiones', 'G2:H100', '#,##0.0000'); fmt('Inversiones', 'J2:L100', MONEY); fmt('Inversiones', 'N2:N4', MONEY);
  fmt('Archivo', 'A2:A', DATE); fmt('Archivo', 'C2:C', MONEY); fmt('Archivo', 'K2:K', MONEY); fmt('Archivo', 'I2:I', '@'); fmt('Archivo', 'L2:L', '@');
  // Resumen y Buscar
  listFrom('Resumen', 'B3', '_Control!B10:B60');
  listOf('Resumen', 'B4', MONTHS.map((_, i) => String(i + 1)), false);
  fmt('Resumen', 'D4:F4', MONEY); fmt('Resumen', 'H4', MONEY); fmt('Resumen', 'G4', '0.0%'); fmt('Resumen', 'B9:D80', MONEY); fmt('Resumen', 'E9:E80', '0%');
  style('Resumen', 'D3:H3', blue); style('Resumen', 'A8:F8', blue); style('Resumen', 'H8:U8', blue);
  fmt('Buscar', 'B4:B5', DATE); fmt('Buscar', 'A10:A3000', DATE); fmt('Buscar', 'C10:C3000', MONEY);
  listFrom('Buscar', 'B6', "'Categorías'!A2:A300"); listFrom('Buscar', 'B7', 'Cuentas!A2:A50');
  style('Buscar', 'A9:G9', blue);
  // Internas
  fmt('_Meta', 'A2:A', '@'); fmt('_Meta', 'C2:C', '@'); fmt('_Alias', 'E2:E', '@');
  widths('Léeme', [900]);
  return { create, values, requests };
}

const LEEME = [
  'Cómo funciona este libro',
  '',
  'Lo normal es usarlo desde la app. Si lo rellenas a mano:',
  '• Movimientos: una fila por movimiento de cualquier cuenta (Fecha, Concepto, Importe, Cuenta). La Categoría es un desplegable; la columna "Sugerida" propone una.',
  '• Compartido: márcalo si el gasto o ingreso es a medias (cuenta al 50 %).',
  '• Mes (opcional): solo si quieres que cuente en otro mes (p. ej. la nómina del día 28 en el mes siguiente). Formato 2026-10.',
  '• Grupo: la misma etiqueta en un gasto y en sus reembolsos; los reembolsos heredan su categoría.',
  '• Columnas grises: se calculan solas. No escribas en ellas.',
  '• Id: lo pone la app.',
  '',
  'Las pestañas ocultas (_Meta, _Reglas, _Alias, _Control) son para la app: no hace falta tocarlas.',
];
